import { api, Banner, dateTime, Empty, PageHead, stackOnMobile, useAsync } from "@getaways/extranet-ui";
import { IconSend } from "@tabler/icons-react";
import { useState } from "react";
import { ExportButtons, formatCell, type Result, type Spec } from "../lib/query";

interface Answer {
  question: string;
  answer: string;
  queries: { spec: Spec; result?: Result; error?: string }[];
}

const EXAMPLES = [
  "Which hotels did we book last month, and through which bed bank?",
  "Room nights and average daily rate per destination this year",
  "Sales per brand in the current CRM period compared with the same days last year",
  "Which agents sold the most this month?",
];

function ResultBlock({ q, title }: { q: Answer["queries"][number]; title: string }) {
  if (q.error) return <p className="small muted">A query was refused: {q.error}</p>;
  const r = q.result!;
  if (!r.rows.length) return <p className="small muted">A query found nothing.</p>;
  return (
    <div className="card" style={{ padding: 0, marginTop: 10 }}>
      <div className="row" style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)" }}><span className="small muted">{r.rows.length} line{r.rows.length === 1 ? "" : "s"}</span><span className="spacer" /><ExportButtons spec={q.spec} title={title} /></div>
      <table ref={stackOnMobile} className="table stack-mobile">
        <thead><tr>{r.columns.map((c) => <th key={c.key} className={c.type === "dimension" ? "" : "num"}>{c.label}</th>)}</tr></thead>
        <tbody>{r.rows.slice(0, 50).map((row, i) => <tr key={i}>{r.columns.map((c) => <td key={c.key} className={c.type === "dimension" ? "" : "num"}>{formatCell(c, row[c.key])}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

/** Questions in natural language (docs/36 §11): answered from the same numbers as the dashboards, with your access. */
export function AskPage() {
  const info = useAsync(() => api<{ enabled: boolean; history: { id: string; question: string; answer: string; at: string }[] }>("GET", "/v1/ask"), []);
  const [question, setQuestion] = useState("");
  const [thread, setThread] = useState<Answer[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const ask = async (text: string) => {
    if (text.trim().length < 3 || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const r = await api<Omit<Answer, "question">>("POST", "/v1/ask", { question: text, history: thread.slice(-4).map((a) => ({ question: a.question, answer: a.answer })) });
      setThread((t) => [...t, { question: text, ...r }]);
      setQuestion("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (info.data && !info.data.enabled) return <><PageHead title="Ask" /><Empty>The assistant is not configured on this server.</Empty></>;
  return (
    <>
      <PageHead title="Ask" sub="Ask about sales, hotels, suppliers or money. Answers use the same numbers as the dashboards, with your access; nothing about customers is shared." />
      {error && <Banner notice={{ tone: "bad", text: error }} />}
      {thread.map((a, i) => (
        <section key={i} className="card" style={{ marginBottom: 12 }}>
          <p className="small muted" style={{ marginTop: 0 }}>{a.question}</p>
          <p style={{ whiteSpace: "pre-wrap" }}>{a.answer}</p>
          {a.queries.map((q, j) => <ResultBlock key={j} q={q} title={a.question.slice(0, 60)} />)}
        </section>
      ))}
      <form className="card row" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); void ask(question); }}>
        <input style={{ flex: 1 }} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={busy ? "Working on it…" : "e.g. Hotels booked in Terceira through Hotelbeds in October, with margin"} disabled={busy} aria-label="Question" maxLength={1000} />
        <button type="submit" disabled={busy || question.trim().length < 3}><IconSend size={16} /> Ask</button>
      </form>
      {!thread.length && (
        <div className="row" style={{ flexWrap: "wrap", gap: 6, marginTop: 10 }}>
          {EXAMPLES.map((e) => <button key={e} className="ghost small" onClick={() => void ask(e)} disabled={busy}>{e}</button>)}
        </div>
      )}
      {!!info.data?.history.length && !thread.length && (
        <section className="card" style={{ marginTop: 14 }}>
          <h2 style={{ marginTop: 0, fontSize: 15 }}>Your recent questions</h2>
          {info.data.history.map((h) => (
            <details key={h.id} style={{ marginBottom: 6 }}><summary className="small">{h.question} <span className="muted">· {dateTime(h.at, "Europe/Lisbon")}</span></summary><p className="small" style={{ whiteSpace: "pre-wrap" }}>{h.answer}</p></details>
          ))}
        </section>
      )}
    </>
  );
}
